/**
 * Indicador de fuerza de la contrasena.
 *
 * Lo que este archivo NO es: la validacion. La validacion la hace el backend con
 * `AUTH_PASSWORD_VALIDATORS` de Django, y es la unica que decide. Esto es una
 * ayuda visual mientras se tipea, para que la persona no descubra recien al
 * enviar que su clave es corta.
 *
 * Los cuatro criterios replican los cuatro validadores configurados en
 * `config/settings.py` —largo minimo, no ser numerica, no ser una de las mas
 * comunes, no parecerse a los datos de la cuenta— para que el consejo en
 * pantalla y el rechazo del servidor digan lo mismo. Si divergieran, el
 * indicador seria peor que no tener ninguno: prometeria que va a entrar algo
 * que el servidor va a rechazar.
 */

export type NivelDeFuerza = 'vacia' | 'debil' | 'aceptable' | 'buena' | 'fuerte';

export interface Fuerza {
  nivel: NivelDeFuerza;
  /** 0 a 4. Alimenta la barra. */
  puntaje: number;
  etiqueta: string;
  /** Lo que falta, en imperativo y en castellano. Vacio cuando no falta nada. */
  consejos: string[];
}

/** Largo minimo de `MinimumLengthValidator`, que en Django viene en 8. */
export const LARGO_MINIMO = 8;

/**
 * Las contrasenas mas usadas del mundo, en la version que importa por aca.
 *
 * La lista real de Django tiene 20.000 entradas y no se va a duplicar en el
 * navegador: esto atrapa las que alguien tipea sin pensar, y para el resto esta
 * el servidor. Que la lista sea corta esta dicho a proposito.
 */
const COMUNES = new Set([
  '12345678',
  '123456789',
  '1234567890',
  'password',
  'contrasena',
  'contraseña',
  'qwertyui',
  'iloveyou',
  'admin123',
  'password1',
  'abc12345',
  '11111111',
  'consorcio',
  'administrador',
]);

function partesDeLaCuenta(email: string, nombre: string): string[] {
  const salida: string[] = [];
  const usuario = email.split('@')[0] ?? '';
  for (const bruto of [usuario, nombre, email]) {
    for (const parte of bruto.split(/[\s._@-]+/)) {
      if (parte.length >= 4) salida.push(parte.toLowerCase());
    }
  }
  return salida;
}

/**
 * Mide una contrasena contra los mismos criterios que aplica el backend.
 *
 * @param email  Email de la cuenta, para el criterio de similitud.
 * @param nombre Nombre y apellido, por lo mismo.
 */
export function medirFuerza(contrasena: string, email = '', nombre = ''): Fuerza {
  if (contrasena === '') {
    return { nivel: 'vacia', puntaje: 0, etiqueta: '', consejos: [] };
  }

  const consejos: string[] = [];
  const minusculas = contrasena.toLowerCase();

  const bastanteLarga = contrasena.length >= LARGO_MINIMO;
  if (!bastanteLarga) consejos.push(`Usá al menos ${LARGO_MINIMO} caracteres.`);

  const soloNumeros = /^\d+$/.test(contrasena);
  if (soloNumeros) consejos.push('No uses sólo números.');

  const esComun = COMUNES.has(minusculas);
  if (esComun) consejos.push('Es una de las contraseñas más usadas del mundo: elegí otra.');

  const seParece = partesDeLaCuenta(email, nombre).some((parte) => minusculas.includes(parte));
  if (seParece) consejos.push('No uses tu nombre ni tu email dentro de la contraseña.');

  // Los cuatro criterios de arriba son ELIMINATORIOS: si alguno falla, el
  // servidor la rechaza, asi que el indicador no puede decir «buena».
  const pasaLoObligatorio = bastanteLarga && !soloNumeros && !esComun && !seParece;
  if (!pasaLoObligatorio) {
    return {
      nivel: 'debil',
      puntaje: 1,
      etiqueta: 'Todavía no sirve',
      consejos,
    };
  }

  // A partir de acá ya entraría. Lo que sigue es cuánto margen tiene.
  let extra = 0;
  if (contrasena.length >= 12) extra += 1;
  if (contrasena.length >= 16) extra += 1;
  const variedad =
    Number(/[a-z]/.test(contrasena)) +
    Number(/[A-Z]/.test(contrasena)) +
    Number(/\d/.test(contrasena)) +
    Number(/[^\w\s]/.test(contrasena));
  if (variedad >= 3) extra += 1;

  if (extra >= 3) {
    return { nivel: 'fuerte', puntaje: 4, etiqueta: 'Muy buena', consejos: [] };
  }
  if (extra >= 1) {
    return {
      nivel: 'buena',
      puntaje: 3,
      etiqueta: 'Buena',
      consejos: contrasena.length < 12 ? ['Con 12 caracteres o más queda mucho más difícil de adivinar.'] : [],
    };
  }
  return {
    nivel: 'aceptable',
    puntaje: 2,
    etiqueta: 'Alcanza',
    consejos: ['Tres o cuatro palabras sueltas son fáciles de recordar y difíciles de adivinar.'],
  };
}

/** Color de la barra segun el nivel. Tokens del tema, no colores sueltos. */
export const COLOR_DE_FUERZA: Record<NivelDeFuerza, string> = {
  vacia: 'bg-line',
  debil: 'bg-danger',
  aceptable: 'bg-warning',
  buena: 'bg-accent',
  fuerte: 'bg-success',
};
